import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';

import { PhxSelectOption, PhxSelectValue } from './../select/select.types';
import { PhxOptionComponent } from '../../lib/select/option.component';
import { PhxDataSource } from '../../lib/services/data-source/phx-data-source.service';
import { PhxSelectionListComponent } from '../select/selection-list.component';
import { PHX_SELECT_OPTION_TOKEN } from './../select/select.token';
import { isNil } from '../../utils';
import mixinDisabled from '../../mixins/disabled';
import { ArrowLeft, Cross } from './../icon/icon-map';
import { PhxSelectDefaultConfig } from './../select/phx-select.common';
import { PhxDialogPlacement } from '../dialog/phx-dialog.types';

const baseSelectMixin = mixinDisabled(
  class {
    constructor(public _elementRef: ElementRef) { }
  }
);

@Component({
  selector: 'phx-mobile-select',
  templateUrl: './mobile-select.component.html',
  styleUrls: ['./mobile-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxMobileSelectComponent),
      multi: true,
    },
  ],
})
export class PhxMobileSelectComponent extends baseSelectMixin {
  @ViewChild(PhxSelectionListComponent) optionList: PhxSelectionListComponent;
  @ContentChildren(PHX_SELECT_OPTION_TOKEN as any) listOfPhxOptions: QueryList<PhxOptionComponent>;

  @Input() public phxOptions: PhxSelectOption[];
  @Input() public phxDataSource: PhxDataSource<PhxSelectOption[]>;
  @Input() public phxPlaceholder: string;
  @Input() public phxSearchPlaceholder: string = 'Search';
  @Input() public phxPrefixIcon: TemplateRef<any>;
  @Input() public phxOpen = false;
  @Input() public phxAllowClear = true;
  @Input() public phxFocusSearchOnOpen = true;
  @Input() public phxCloseOnClear = false;
  @Input() public phxLoading = false;
  @Input() public phxLabelTemplate: TemplateRef<{ $implicit: any }>;
  @Input() public phxTriggerTemplate: TemplateRef<{ $implicit: PhxSelectOption | PhxSelectOption[] }>;
  @Input() public phxMultiple: boolean = false;
  @Input() public phxResultNotFound: TemplateRef<any> | string = 'No results found';
  @Input() public phxShowRadio: boolean = false;
  @Input() public phxShowSubText: boolean = false;
  @Input() public phxFilterPredicate: (searchTerm: string, opt: PhxSelectOption) => boolean;
  @Input() public phxOptionHeight: number; // phxOptionHeight is not working, see this
  @Input() public phxDropdownHeight: number = PhxSelectDefaultConfig.maxHeightOfDropdown;
  @Input() public phxDropdownWidth: number = null;
  @Input() public phxOptionsListClassName: string = '';
  @Input() public phxEmitOnlyIndependentSelections: boolean = false;
  @Input() public phxSearchDebounceTime: number = 300; //done
  @Input() public phxSelectionChangeDebounceTime: number = null;
  @Input() public phxOptimisticSelection: boolean = false;
  @Input() public phxAllowSearch: boolean = true;
  @Input() public phxFullHeight: boolean = true;
  @Input() public phxHeaderTitle: string;
  @Input() public phxPlacement: PhxDialogPlacement = 'top';
  @Input() public phxServerSearch: boolean = false;
  @Input() public phxFooterRightTemplate: TemplateRef<any>;
  @Input() public phxPreventSelection: boolean = false;
  @Input() public phxVirtualScrollEnabled: boolean = true;
  @Input() public phxTruncateSubText: boolean = true;
  @Input() public phxHighlightSubtext: boolean = false;
  @Input() public phxSearchText: string;

  @Output() public readonly phxLoadingChange = new EventEmitter<boolean>();
  @Output() public readonly phxOpenChange = new EventEmitter<boolean>();
  @Output() public readonly phxSearchChange = new EventEmitter<string>();
  @Output() public readonly phxSelectionChange = new EventEmitter<PhxSelectOption | PhxSelectOption[]>();
  @Output() public readonly phxClear = new EventEmitter<void>();
  @Output() public readonly phxScrollEnd = new EventEmitter<void>();
  @Output() public readonly phxFocus = new EventEmitter<void>();
  @Output() public readonly phxBlur = new EventEmitter<void>();
  public searchValue: string = '';
  public disabled: boolean;
  public selectedValue: any;
  public searchedOptions: PhxSelectOption[];
  public searched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public focused: boolean;
  public height: number = 0;
  public dropdownWidth: string | number = 'auto';
  public ArrowLeftIcon = ArrowLeft;
  public CrossIcon = Cross;
  public cssClassMap: Map<string, boolean> = new Map<string, boolean>();
  public onChange = (val) => {};
  public onTouched = () => {};
  private _destroy$ = new Subject<void>();

  public get selectLabel(): string {
    return this.optionList.selectedItemsCount >= 2 ? this.phxPlaceholder : this.optionList.firstSelectedOption?.label;
  }

  private transformProjectedOptions(content: PhxOptionComponent[]): PhxSelectOption[] {
    return content.map(
      (item: PhxOptionComponent): PhxSelectOption => ({
        label: item.label,
        value: item.value,
        subText: item.subText,
        groupLabel: item.groupLabel,
        disabled: item.disabled,
        data: item.data,
        optionTemplate: item.phxOptionTemplate,
      })
    );
  }

  public get selectedOptionCount(): number {
    return this.optionList?.selectedItemsCount || 0;
  }
  public get showFooter(): boolean {
    return this.selectedOptionCount > 0;
  }

  public get headerTitle(): string {
    return this.phxHeaderTitle || this.optionList?.headerTitle || this.phxPlaceholder;
  }

  public get headerSubTitle(): string {
    return this.optionList?.headerTitle ? this.phxPlaceholder : '';
  }

  public get isNestedOptions(): boolean {
    return this.optionList?.isNestedOptions;
  }

  public onValueChange(value: any) {
    this.selectedValue = value;
    this.onChange(value);
  }

  public onClear() {
    this.phxClear.emit();
  }

  public writeValue(value: PhxSelectValue | PhxSelectOption | PhxSelectValue[] | PhxSelectOption[]): void {
    if (isNil(value)) return;
    this.selectedValue = value;
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public removeTag(event: MouseEvent | KeyboardEvent, option: PhxSelectOption, index: number): void {
    event.stopPropagation();
    this.optionList.handleOptionClick(option, index);
  }

  public searchChange(event: Event) {
    this.optionList.debouncedOnSearchChange(event);
  }

  public onBackClick() {
    if (this.optionList.isRootLevel) {
      this.phxOpen = false;
      this.phxOpenChange.emit(this.phxOpen);
    } else this.optionList.showParent();
  }

  public onClearClick() {
    this.optionList.clearOption();
  }

  public onOpenChange(open: boolean) {
    this.phxOpenChange.emit(open);
    if (open) {
      this.optionList.virtualScrollToIndex();
    }
  }

  public onBackdropClicked() {
    this.phxOpen = false;
  }

  constructor(host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
    super(host);
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    const { phxDropdownHeight, phxOptionHeight, phxOptionsListClassName, phxSearchText } = simpleChanges;

    if (phxDropdownHeight) {
      this.optionList?.adjustViewportHeightForOptions();
    }

    if (phxOptionHeight) {
      this.phxOptionHeight = this.phxOptionHeight || PhxSelectDefaultConfig.optionHeight;
      this.optionList?.adjustViewportHeightForOptions();
    }

    if (phxOptionsListClassName) {
      this.phxOptionsListClassName.split(' ').forEach((cssClass) => {
        this.cssClassMap.set(cssClass, true);
      })
    }

    if (phxSearchText) {
      this.searchValue = this.phxSearchText || '';
    }
  }

  ngAfterContentInit(): void {
    if (this.listOfPhxOptions.length) {
      this.phxOptions = this.transformProjectedOptions(this.listOfPhxOptions.toArray());
    }
    this.listOfPhxOptions.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.phxOptions = this.transformProjectedOptions(this.listOfPhxOptions.toArray());
    });
  }

  public ngAfterViewInit(): void {
    const heightOfHeaderAndSearch = 124;
    this.height = window.innerHeight - heightOfHeaderAndSearch;

    window.addEventListener('resize', () => {
      this.height = window.innerHeight - heightOfHeaderAndSearch;
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
